<script lang="ts" setup>
import { computed } from 'vue';
import { Reason } from '@ilteducation/customer-mgmt-permissions';
import { useLocale } from '../composables/useLocale';
import { useComboboxName } from '../composables/useComboboxName';
import { useEscalateLogin } from '../composables/useEscalateLogin';
import { isFixableByEscalatedLogin } from '../helpers/permissions';
import { RoleOption } from '../composables/useUserRoles';
import { Role } from '../types/user';

type Props = {
  options: RoleOption[];
  /** When set, a non-role entry with this title is prepended, e.g. "Select role". */
  placeholderTitle?: string;
};

const props = defineProps<Props>();
const model = defineModel<Role | null>();

const { t } = useLocale();
const { requestEscalatedLogin } = useEscalateLogin();

type Entry = {
  value: Role | null;
  title: string;
  allowed: boolean;
  reason?: Reason;
};

/**
 * Every role the organization can have is listed, including the ones the caller may not pick: a
 * hidden option looks like a missing feature, while a padlocked one tells the caller what is in
 * the way. Roles the organization type does not have never reach here, since no login would
 * unlock them.
 */
const entries = computed<Entry[]>(() => [
  ...(props.placeholderTitle ? [{ value: null, title: props.placeholderTitle, allowed: true }] : []),
  ...props.options.map((option) => ({
    value: option.type,
    title: t(option.transKey),
    allowed: option.allowed,
    reason: option.reason,
  })),
]);

const isActionable = (entry: Entry) => isFixableByEscalatedLogin(entry.reason);

const lockedText = (entry: Entry) => {
  const reasonText = entry.reason ? t(`lcp.permissions.${entry.reason}`) : t('lcp.permissions.not_permitted');
  return isActionable(entry) ? `${reasonText} ${t('lcp.permissions.escalate_login_hint')}` : reasonText;
};

const onLockedClick = (entry: Entry) => {
  if (isActionable(entry)) {
    requestEscalatedLogin();
  }
};

// Nothing names this field: callers place it in a role column or beside their own text, neither of
// which is tied to the input. It has to be named twice over, the input and the combobox both.
const menuProps = useComboboxName(() => t('lcp.select_role'));
</script>

<template>
  <v-select
    v-model="model"
    :items="entries"
    item-title="title"
    item-value="value"
    :aria-label="t('lcp.select_role')"
    :menu-props="menuProps"
  >
    <template #item="{ item, props: itemProps }">
      <v-list-item v-if="item.raw.allowed" v-bind="itemProps"></v-list-item>
      <!--
        A `disabled` v-list-item drops all pointer events, so neither the tooltip nor the
        escalated-login click would ever fire. The locked entry stays interactive instead and
        simply never selects itself.
      -->
      <v-tooltip v-else :text="lockedText(item.raw)" location="end" :open-on-click="false" interactive>
        <template #activator="{ props: activatorProps }">
          <v-list-item
            v-bind="activatorProps"
            :title="item.raw.title"
            append-icon="mdi-lock"
            role="option"
            aria-disabled="true"
            class="role-select__locked"
            :class="{ 'role-select__locked--actionable': isActionable(item.raw) }"
            @click.stop="onLockedClick(item.raw)"
          ></v-list-item>
        </template>
      </v-tooltip>
    </template>
  </v-select>
</template>

<style lang="scss" scoped>
.role-select__locked {
  cursor: default;

  :deep(.v-list-item-title) {
    opacity: var(--v-disabled-opacity);
  }
}

.role-select__locked--actionable {
  cursor: pointer;

  /* Vuetify mutes append icons to medium emphasis; the padlock is the affordance, so keep it full strength. */
  :deep(.v-list-item__append .v-icon) {
    color: rgb(var(--v-theme-primary));
    opacity: 1;
  }
}
</style>
